/** Connected account id for Composio trigger instances (ca_*). */
export function getConnectionAccountId(connection) {
  if (!connection) return '';
  return (
    connection.connected_account_id ||
    connection.connectedAccountId ||
    connection.connection_id ||
    connection.connectionId ||
    connection.id ||
    ''
  );
}

export function getConnectionToolkitSlug(connection) {
  if (!connection) return '';
  return (
    connection.toolkit_slug ||
    connection.toolkitSlug ||
    connection.app_key ||
    connection.appKey ||
    connection.app_name ||
    connection.appName ||
    connection.name ||
    ''
  ).toString().toLowerCase();
}

export function getConnectionDisplayName(connection) {
  if (!connection) return 'Connected app';
  return (
    connection.toolkit_name ||
    connection.toolkitName ||
    connection.app_name ||
    connection.appName ||
    connection.name ||
    getConnectionToolkitSlug(connection) ||
    'Connected app'
  );
}

export function normalizeTriggerTypesList(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.trigger_types || data.types || data.items || [];
}

export function getTriggerTypeSlug(item) {
  if (!item) return '';
  if (typeof item === 'string') return item;
  return item.slug || item.trigger_slug || item.name || item.id || '';
}

export function getTriggerTypeLabel(item) {
  if (!item) return '';
  if (typeof item === 'string') return item;
  return item.display_name || item.displayName || item.name || item.slug || item.id || '';
}

/**
 * Build default JSON for composio_trigger_config from trigger type schema.
 */
export function buildConfigPlaceholderFromSchema(schema) {
  if (!schema || typeof schema !== 'object') return '{}';
  const props = schema.properties || schema.config?.properties || schema.config_schema?.properties;
  if (!props || typeof props !== 'object') return '{}';
  const sample = {};
  Object.entries(props).forEach(([key, def]) => {
    if (def?.default !== undefined) {
      sample[key] = def.default;
      return;
    }
    const t = def?.type;
    if (t === 'string') sample[key] = '';
    else if (t === 'number' || t === 'integer') sample[key] = 0;
    else if (t === 'boolean') sample[key] = false;
    else if (t === 'array') sample[key] = [];
    else sample[key] = null;
  });
  try {
    return JSON.stringify(sample, null, 2);
  } catch {
    return '{}';
  }
}

export function extractComposioFromTrigger(trigger) {
  const cfg = trigger?.trigger_config || {};
  const instanceId =
    trigger?.composio_trigger_instance_id ||
    cfg.composio_trigger_instance_id ||
    '';
  const slugs = Array.isArray(cfg.composio_trigger_slugs)
    ? cfg.composio_trigger_slugs.filter(Boolean)
    : [];
  const slug = slugs[0] || cfg.composio_trigger_slug || '';
  const connectedAccountId = cfg.connected_account_id || '';
  const composioConfig = cfg.composio_trigger_config || {};
  const configJson =
    composioConfig && Object.keys(composioConfig).length
      ? JSON.stringify(composioConfig, null, 2)
      : '';
  return { instanceId, slug, slugs, connectedAccountId, composioConfig, configJson };
}

export const COMPOSIO_DEFAULT_QUERY =
  'A new event occurred. Review the details below and take the appropriate action.\n\nEvent: {{payload.metadata.trigger_slug}}\n\nDetails:\n{{payload.data}}';

/** Normalize API trigger records for consistent UI filtering. */
export function normalizeTriggerItem(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  return {
    ...raw,
    id: raw.id ?? raw.trigger_id,
    agent_id: raw.agent_id ?? raw.agentId ?? raw.agent?.id,
    is_active: raw.is_active ?? raw.isActive ?? raw.enabled,
    trigger_type: raw.trigger_type ?? raw.triggerType,
    trigger_config: raw.trigger_config ?? raw.triggerConfig ?? {},
    query_template: raw.query_template ?? raw.queryTemplate ?? '',
    webhook_url: raw.webhook_url ?? raw.webhookUrl,
    name: raw.name ?? '',
  };
}

export function isTriggerActive(item) {
  const value = item?.is_active ?? item?.isActive ?? item?.enabled;
  return value === true || value === 1 || value === '1' || value === 'true';
}

export function getTriggerAgentId(item) {
  const id = item?.agent_id ?? item?.agentId ?? item?.agent?.id;
  return id === undefined || id === null ? '' : String(id);
}

/** Config schema object from a GET /composio/triggers/types/{slug} response. */
export function getConfigSchemaFromTriggerType(data) {
  if (!data || typeof data !== 'object') return null;
  return data.config_schema || data.config || data.schema || data;
}

/** Optional ingest fields shown in Event setup even when not schema-required. */
export const INGEST_VISIBLE_OPTIONAL_FIELDS = ['folder_id'];

export function isNewFileMatchingQuerySlug(slug = '') {
  return String(slug).toUpperCase().includes('NEW_FILE_MATCHING_QUERY');
}

export function slugSupportsIngestField(schemaData, fieldKey) {
  const schema = getConfigSchemaFromTriggerType(schemaData);
  return Boolean(schema?.properties?.[fieldKey]);
}

export function filterSlugsForIngestField(schemasBySlug = {}, slugs = [], fieldKey) {
  return slugs.filter((slug) => slugSupportsIngestField(schemasBySlug[slug], fieldKey));
}

export function getIngestOptionalFieldKeys(eventSlug = '', schema) {
  const keys = [...INGEST_VISIBLE_OPTIONAL_FIELDS];
  if (isNewFileMatchingQuerySlug(eventSlug) && schema?.properties?.query) {
    keys.push('query');
  }
  return keys;
}

const schemaFieldFromDef = (key, def, { required = false } = {}) => ({
  key,
  label: (def.title || key).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
  description: def.description || '',
  type: def.type || 'string',
  required,
  enum: def.enum,
  default: def.default,
  examples: Array.isArray(def.examples) ? def.examples : undefined,
});

export function getIngestFieldPlaceholder(field) {
  if (!field?.examples?.length) return undefined;
  return String(field.examples[0]);
}

const DRIVE_QUERY_SYNTAX_HINT =
  /\b(contains|in parents|mimeType|modifiedTime|createdTime|trashed|starred|fullText)\b|[=<>!]=?/i;

/**
 * Google Drive files.list `q` must use Drive query syntax.
 * Plain keywords (e.g. "report") are wrapped as name contains 'report'.
 */
export function normalizeDriveQueryInput(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return '';

  if (DRIVE_QUERY_SYNTAX_HINT.test(trimmed)) {
    return trimmed;
  }

  const escaped = trimmed.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
  return `name contains '${escaped}'`;
}

/**
 * Merge required fields across multiple trigger-type responses (ingest multi-event).
 */
export function extractRequiredSchemaFields(triggerTypeResponses = []) {
  const fieldMap = new Map();

  triggerTypeResponses.forEach((data) => {
    const schema = getConfigSchemaFromTriggerType(data);
    if (!schema?.properties) return;

    const requiredList = Array.isArray(schema.required) ? schema.required : [];
    requiredList.forEach((key) => {
      const def = schema.properties[key];
      if (!def || fieldMap.has(key)) return;
      fieldMap.set(key, schemaFieldFromDef(key, def, { required: true }));
    });
  });

  return Array.from(fieldMap.values());
}

/**
 * Ingest Event setup: required fields plus optional fields like folder_id.
 */
export function extractIngestSchemaFields(triggerTypeResponses = []) {
  const fieldMap = new Map();

  triggerTypeResponses.forEach((data) => {
    const schema = getConfigSchemaFromTriggerType(data);
    if (!schema?.properties) return;

    const requiredList = Array.isArray(schema.required) ? schema.required : [];

    requiredList.forEach((key) => {
      const def = schema.properties[key];
      if (!def || fieldMap.has(key)) return;
      fieldMap.set(key, schemaFieldFromDef(key, def, { required: true }));
    });

    INGEST_VISIBLE_OPTIONAL_FIELDS.forEach((key) => {
      const def = schema.properties[key];
      if (!def || fieldMap.has(key)) return;
      fieldMap.set(key, schemaFieldFromDef(key, def, { required: false }));
    });
  });

  return Array.from(fieldMap.values());
}

/**
 * Flat ingest setup fields merged across selected events (no per-event labels in UI).
 */
export function getMergedIngestSetupFields(schemasBySlug = {}) {
  const fieldMap = new Map();

  Object.entries(schemasBySlug).forEach(([slug, data]) => {
    extractIngestSchemaFieldsForEvent(data, slug).forEach((field) => {
      if (!fieldMap.has(field.key)) {
        fieldMap.set(field.key, { field, slugs: [slug] });
        return;
      }
      const entry = fieldMap.get(field.key);
      if (!entry.slugs.includes(slug)) entry.slugs.push(slug);
      if (field.required) entry.field = { ...entry.field, required: true };
    });
  });

  return Array.from(fieldMap.values());
}

/**
 * Ingest Event setup fields for a single trigger-type response.
 */
export function extractIngestSchemaFieldsForEvent(triggerTypeResponse, eventSlug = '') {
  const schema = getConfigSchemaFromTriggerType(triggerTypeResponse);
  if (!schema?.properties) return [];

  const slug = eventSlug || getTriggerTypeSlug(triggerTypeResponse) || '';
  const fieldMap = new Map();
  const requiredList = Array.isArray(schema.required) ? schema.required : [];

  requiredList.forEach((key) => {
    const def = schema.properties[key];
    if (!def || fieldMap.has(key)) return;
    fieldMap.set(key, schemaFieldFromDef(key, def, { required: true }));
  });

  getIngestOptionalFieldKeys(slug, schema).forEach((key) => {
    const def = schema.properties[key];
    if (!def || fieldMap.has(key)) return;
    const required = key === 'query' && isNewFileMatchingQuerySlug(slug);
    fieldMap.set(key, schemaFieldFromDef(key, def, { required }));
  });

  return Array.from(fieldMap.values());
}

export function getTriggerTypeEventName(data, fallbackSlug = '') {
  if (!data) return fallbackSlug;
  return data.name || data.display_name || data.displayName || fallbackSlug;
}

/**
 * Per-event ingest setup groups for the form UI.
 * @param {Record<string, object>} schemasBySlug
 * @param {(slug: string) => string} [resolveLabel]
 */
export function buildIngestEventSetupGroups(schemasBySlug = {}, resolveLabel) {
  return Object.entries(schemasBySlug)
    .map(([slug, data]) => ({
      slug,
      eventName: getTriggerTypeEventName(data, resolveLabel?.(slug) || slug),
      fields: extractIngestSchemaFieldsForEvent(data, slug),
    }))
    .filter((group) => group.fields.length > 0);
}

export function buildIngestComposioConfigForEvent(
  triggerTypeResponse,
  userValues = {},
  setupFields = [],
  options = {},
) {
  if (!triggerTypeResponse) return {};
  return buildIngestComposioConfig([triggerTypeResponse], userValues, setupFields, options);
}

export function coerceSchemaFieldValue(raw, type) {
  if (type === 'integer' || type === 'number') return Number(raw);
  if (type === 'boolean') return raw === true || raw === 'true';
  return raw;
}

const INGEST_MAX_RESULTS_DEFAULT = 5;

/**
 * Build composio_trigger_config for ingest mode: user fills required fields only;
 * optional fields (interval, max_results, etc.) are applied from schema defaults.
 */
export function buildIngestComposioConfig(
  triggerTypeResponses = [],
  userValues = {},
  setupFields = [],
  { maxResultsDefault = INGEST_MAX_RESULTS_DEFAULT } = {},
) {
  const allProps = {};

  triggerTypeResponses.forEach((data) => {
    const schema = getConfigSchemaFromTriggerType(data);
    if (!schema?.properties) return;
    Object.entries(schema.properties).forEach(([key, def]) => {
      if (!allProps[key]) allProps[key] = def;
    });
  });

  const config = {};

  Object.entries(allProps).forEach(([key, def]) => {
    if (key === 'max_results') {
      config[key] = maxResultsDefault;
      return;
    }
    if (def?.default !== undefined) {
      config[key] = def.default;
    }
  });

  setupFields.forEach((field) => {
    const raw = userValues[field.key];
    if (raw === undefined || raw === '') return;
    let normalized = field.type === 'string' ? String(raw).trim() : raw;
    if (normalized === '') return;
    if (field.key === 'query') {
      normalized = normalizeDriveQueryInput(normalized);
    }
    config[field.key] = coerceSchemaFieldValue(normalized, field.type);
  });

  return config;
}

export { INGEST_MAX_RESULTS_DEFAULT };

/**
 * Turn Composio trigger type config schema into simple form fields.
 */
export function extractSchemaFields(schema) {
  if (!schema || typeof schema !== 'object') return [];
  const props =
    schema.properties ||
    schema.config?.properties ||
    schema.config_schema?.properties;
  if (!props || typeof props !== 'object') return [];

  const requiredList = Array.isArray(schema.required)
    ? schema.required
    : Array.isArray(schema.config?.required)
      ? schema.config.required
      : [];

  return Object.entries(props).map(([key, def]) => ({
    key,
    label: (def?.title || key).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    description: def?.description || '',
    type: def?.type || 'string',
    required: requiredList.includes(key),
    enum: def?.enum,
    default: def?.default,
  }));
}

export function parseComposioConfigObject(jsonOrObject) {
  if (!jsonOrObject) return {};
  if (typeof jsonOrObject === 'object' && !Array.isArray(jsonOrObject)) return jsonOrObject;
  const trimmed = String(jsonOrObject).trim();
  if (!trimmed || trimmed === '{}') return {};
  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}
